import * as React from "react";
import { AnalysisAcceptabilityStatus } from "../../misc/components/analysis/AnalysisAcceptabilityStatus";
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import uniqBy from "lodash-es/uniqBy";
import { Column, Table } from "../../Components/QueryTable";
import { useQuery, getAnalysisSetsQcBlindingFilter } from "../../Components/Query";
import { MatchSortHeader } from "../../Components/MatchSortHeader";
import { LungVolumeAnalysisSet, WebPortalClient } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useSecurityContext } from "../../Core/SecurityContext";
import { DateMatchSortHeader } from "../../Components/DateMatchSortHeader";
import { HamburgerMenu } from '../../misc/components/HamburgerMenu';
import { Link } from "react-router-dom";
import Condition from "../../misc/components/Condition";
import { Authorization } from "../../misc/components/Authorization";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { Permission, OverreadStatusIdEnum, User, OverreadChallengeStatusEnum } from "../../misc/api/apiTypes";
import { LungVolumeAnalysisSetTypeEnum } from '../../misc/api/ApiLungVolumeAnalysisSetType';
import { AnalysisSetsTableAutomationId, getAnalysisSetsTableAutomationId, getQueryTableAutomationId, QueryTableAutomationId } from "../../misc/Helpers/AutomationIdHelper";
import { AnalysisSetsOverreadStatusHeader } from "../../Components/AnalysisSetsOverreadStatusHeader";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { AssignToUserInGroupModal } from "../../misc/components/AssignToUserInGroupModal";
import { SessionChallengeStatus } from '../../misc/components/SessionChallengeStatus';
import { createFormatters } from "../Shared/AnalysisHelpers";

declare const rootUrl: string;

export interface LVAnalysisListTableProps {
}

export const LungVolumeAnalysisListTable = (props: LVAnalysisListTableProps) => {
    const client = useWebPortalApiClient();
    const { study, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const { subjectTypes, overreadStatuses, deviceTypes } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [subjectTypeOptions, setSubjectTypeOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [serialNumbers, setSerialNumbers] = React.useState([]);
    const [analystSetTypes, setAnalystSetTypes] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([]);
    const [overreadChallengeStatuses, setOverreadChallengeStatuses] = React.useState([]);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);
    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);
    const { data, applyFilters, filters, refresh } = useQuery();
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `study/id eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/study/id eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getSubjectTypes = async () => {

            const subjectsTypes = await client.client.getSubjectTypes("id,name");
            const options = subjectsTypes.value.map(s => ({
                value: s.id,
                label: s.name,
            }));

            setSubjectTypeOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getLungVolumeSessions("visit", `visit/subject/site/study/id eq ${study.id}`, "visit/name", "visit($select=name)");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getLungVolumeSessions("name", `visit/subject/site/studyId eq ${study.id}`, "name");
            const options = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);
        };

        const getDeviceSerials = async () => {

            const serialNumbers = await client.client.getLungVolumeSessions("serialNumber", `visit/subject/site/studyId eq ${study.id}`, "serialNumber");
            const options = uniqBy(serialNumbers.value, o => o.serialNumber).map(s => ({
                value: s.serialNumber,
                label: s.serialNumber,
            }));

            setSerialNumbers(options);
        };

        const getAnalystSetTypes = async () => {
            const types = await legacyClient.getLungVolumeAnalysisSetTypes();
            const options = uniqBy(types, o => o.Id).map(s => ({
                value: s.Id,
                label: s.Name,
            }));

            setAnalystSetTypes(options);
        };

        const getAnalysts = async () => {

            const analysts = await client.client.getLungVolumeAnalysisSets("analystId,analystEmail", `session/visit/subject/site/studyId eq ${study.id}`, "analyst");

            const options = uniqBy(analysts.value, o => o.analystId).filter(o => o.analystEmail).map(s => ({
                value: s.analystId,
                label: s.analystEmail,
            }));

            setAnalysts(options);
        };

        const getOverreadChallengeStatuses = async () => {
            const overreadChallengeStatuses = await client.client.getOverreadChallengeStatuses();

            const options = uniqBy(overreadChallengeStatuses.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadChallengeStatuses(options);
        };

        getSites();
        getSubjects();
        getSubjectTypes();
        getVisits();
        getSessions();
        getDeviceSerials();
        getAnalystSetTypes();
        getAnalysts();
        getOverreadChallengeStatuses();

    }, [study.id]);

    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    const RenderHamburgerMenu = (analysisSet: LungVolumeAnalysisSet, currentPermissions: Permission[], currentStudyId: string, currentUser: User) => {
        return (
            <HamburgerMenu contextMenuLeftAligned={true} automationId={`${getQueryTableAutomationId(QueryTableAutomationId.Options)}_${analysisSet.id}`}>
                <Authorization permissionId={PermissionEnum.AccessSingleLungVolumeSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <li>
                        <Link to={`/LungVolume/Analysis/${analysisSet.id}/view`} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.ViewAnalysis)}_${analysisSet.id}`}>View</Link>
                    </li>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadLungVolumeSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >

                    <Condition test={() => analysisSet !== null
                        && analysisSet.type !== LungVolumeAnalysisSetTypeEnum.Site
                        && (!analysisSet.analystId || analysisSet.analystId === currentUser.Id)
                    }>
                        <li>
                            <Link to={`/LungVolume/Analysis/${analysisSet.id}/overread`} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.OverreadAnalysis)}_${analysisSet.id}`}>Over-read</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadLungVolumeSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === LungVolumeAnalysisSetTypeEnum.Overread && (analysisSet.status === OverreadStatusIdEnum.Pending || analysisSet.status === OverreadStatusIdEnum.NotStarted)}>
                        <li>
                            <a onClick={() => { setSessionIdToReassign(analysisSet.id); setReassignToUserModalVisible(true); }}>Reassign</a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    const clearAllFilters = () => {
        applyFilters([]);
    }

    if (subjectTypes.length === 0 || overreadStatuses.length === 0)
        return <LoadingWithMessage message="Loading Lung Volume Analysis Sets..." />

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (analysisSet: LungVolumeAnalysisSet) => {
        return (<>
            <AnalysisAcceptabilityStatus value={analysisSet.status} /><br />
            <SessionChallengeStatus challengeIsClosed={analysisSet.session.challengeIsClosed} challengeStatus={analysisSet.session.overreadChallengeStatus} analysisOverreadStatus={analysisSet.status} />
        </>);
    }

    const formatAnalystSetType = (type: string) => {
        switch (type) {
            case LungVolumeAnalysisSetTypeEnum.Overread:
                return "Overread";
            case LungVolumeAnalysisSetTypeEnum.Site:
                return "Site";
            case LungVolumeAnalysisSetTypeEnum.QC:
                return "QC";
        }
    }

    const isQcAnalysisSet = (set: LungVolumeAnalysisSet): boolean => set.type === LungVolumeAnalysisSetTypeEnum.QC;

    const lvFormatters = createFormatters<LungVolumeAnalysisSet>(isQcAnalysisSet);

    const handleReassign = async (sessionId: string, userId: string) => {
        await legacyClient.reassignLungVolume(sessionId, userId);
        setReassignToUserModalVisible(false);
        refresh();
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Lung Volume Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.ClearAllFilters)}`}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div id="AnalysisSetsTable" className="table-responsive" data-automationid="AnalysisSetsTable" style={{ minHeight: "900px" }}>
                                <Table data={data} automationId="LungVolumeAnalysisListTable">
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SiteNumber)}`} header={<MatchSortHeader title="Site ID" field="session/visit/subject/site/id" options={siteOptions} sortField="session/visit/subject/site/number" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SiteNumber)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => lvFormatters.formatSiteId(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectNumber)}`} header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="session/visit/subject/id" options={subjectOptions} sortField="session/visit/subject/number" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectNumber)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => lvFormatters.formatSubjectId(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectType)}`} header={<MatchSortHeader title={fieldDisplayText.SubjectType} field="session/visit/subject/typeKey" options={subjectTypeOptions} sortField="session/visit/subject/typeName" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectType)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => lvFormatters.formatSubjectType(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.VisitName)}`} header={<MatchSortHeader title="Visit" field="session/visit/name" options={visitOptions} sortField="session/visit/name" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.VisitName)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => lvFormatters.formatVisitLabel(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SessionLabel)}`} header={<MatchSortHeader title="Session" field="session/name" options={sessionOptions} sortField="session/name" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SessionLabel)}`} />} formatter={set => lvFormatters.formatSessionLabel(set)} />
                                    <Column field="type" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.AnalysisSetType)}`} header={<MatchSortHeader title="Analysis Set Type" field="type" options={analystSetTypes} automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.AnalysisSetType)}`} />} formatter={type => formatAnalystSetType(type)} />
                                    <Column field="created" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.CreatedAt)}`} header={<DateMatchSortHeader title="Created At" field="created" sortField="created" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.CreatedAt)}`} />} formatter={created => formatDateTime(created)} />
                                    <Column field="started" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.StartedAt)}`} header={<DateMatchSortHeader title="Started At" field="started" sortField="started" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.StartedAt)}`} />} formatter={started => formatDateTime(started)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Status)}`} 
                                        header={<AnalysisSetsOverreadStatusHeader
                                            title="Status"
                                            sortField="status"
                                            automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Status)}`}
                                            overreadStatuses={statusesOptions}
                                            overreadChallengeStatuses={overreadChallengeStatuses}
                                        />}
                                        formatter={(analysisSet: LungVolumeAnalysisSet) => formatStatus(analysisSet)}
                                    />
                                    {ViewOverreaderName()
                                        ? <Column field="analystEmail" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Overreader)}`} header={<MatchSortHeader title="Over-reader" field="analystId" options={analysts} sortField="analystEmail" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Overreader)}`} />} formatter={analyst => formatAnalyst(analyst)} />
                                        : null
                                    }
                                    <Column field="" formatter={analysisSet => RenderHamburgerMenu(analysisSet, currentUserPermissions, study.id, currentUser)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AssignToUserInGroupModal show={isReassignToUserModalVisible} headerText="Reassign Analysis Set" permission={PermissionEnum.OverreadLungVolumeSession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user) => { handleReassign(sessionIdToReassign, user) }} />
        </>
    );
}
